/**
 * Cprepage Component
 * Full-screen pre-loading screen with centered triangle logo
 * Displays while viewport is being determined
 */
'use client';
import { motion } from 'framer-motion';
import { Cimage } from '../Cimage';
import { ColorType } from '@/theme/colors';
import { DomElementId } from '@/constants/domIds';
const CPREPAGE_TRIANGLE_RENDERED_WIDTH = 160;
const NEUTRAL_EMPTY_IMAGE_WIDTH = 499;
const NEUTRAL_EMPTY_IMAGE_HEIGHT = 446;
// Triangle entrance: blurred + scaled-up at opacity 0 → sharp + scale 1 at full opacity.
const CPREPAGE_TRIANGLE_ENTRANCE_DURATION_SEC = 0.7;
const CPREPAGE_TRIANGLE_VISIBLE_OPACITY = 0.6;
// Triangle exit: scales up + blurs + fades out. Linear easing keeps opacity in
// lock-step with scale — with an ease-in-out the opacity would collapse to ~0 by
// the time the scale was halfway, making the motion invisible.
const CPREPAGE_TRIANGLE_EXIT_DURATION_SEC = 0.7;
const CPREPAGE_TRIANGLE_EXIT_SCALE = 3;
const CPREPAGE_TRIANGLE_EXIT_BLUR_PX = 8;
// Background fade: starts at the same instant as the triangle's exit but completes
// FIRST (before the triangle finishes). The triangle's last ~200ms of scale-out +
// fade happens on the already-revealed page, so the overlay dissolves through the
// page rather than yanking away after the motion stops.
const CPREPAGE_BACKGROUND_FADE_DELAY_SEC = 0;
const CPREPAGE_BACKGROUND_FADE_DURATION_SEC = 0.5;
const CPREPAGE_ENTRANCE_EASE = [0.4, 0, 0.2, 1] as const;
const CPREPAGE_EXIT_EASE = 'linear';
export interface CprepageProps {
}
export function Cprepage({}: CprepageProps) {
    return <motion.div data-el="Cprepage/Root" id={DomElementId.PrepageRoot} initial={{ opacity: 1 }} animate={{ opacity: 1 }}
    // Background sits at full opacity until the triangle's exit animation has
    // completed, then fades out quickly. Detaching the two transitions is what
    // makes the triangle's scale-up + blur visible — if the background faded
    // simultaneously, the triangle's opacity would multiply with it and the
    // motion would be invisible.
    exit={{
            opacity: 0,
            transition: {
                delay: CPREPAGE_BACKGROUND_FADE_DELAY_SEC,
                duration: CPREPAGE_BACKGROUND_FADE_DURATION_SEC,
                ease: CPREPAGE_ENTRANCE_EASE,
            },
        }} style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: ColorType.Black,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
        }}>
            <motion.div data-el="Cprepage/Triangle" initial={{ opacity: 0, scale: 1.5, filter: 'blur(5px)' }} animate={{
            opacity: CPREPAGE_TRIANGLE_VISIBLE_OPACITY,
            scale: 1,
            filter: 'blur(0px)',
            transition: {
                duration: CPREPAGE_TRIANGLE_ENTRANCE_DURATION_SEC,
                ease: CPREPAGE_ENTRANCE_EASE,
            },
        }} exit={{
            opacity: 0,
            scale: CPREPAGE_TRIANGLE_EXIT_SCALE,
            filter: `blur(${CPREPAGE_TRIANGLE_EXIT_BLUR_PX}px)`,
            transition: {
                duration: CPREPAGE_TRIANGLE_EXIT_DURATION_SEC,
                ease: CPREPAGE_EXIT_EASE,
            },
        }}
    // Note: no inline `opacity` here. Framer-motion controls every animated
    // property; a static inline `opacity: 0.6` would conflict with the
    // animation pipeline on hydration and leave the triangle stuck at the
    // initial state.
    style={{
            width: CPREPAGE_TRIANGLE_RENDERED_WIDTH,
            height: 'auto',
        }}>
                <Cimage src="/triangles/neutral-empty.png" alt="" width={NEUTRAL_EMPTY_IMAGE_WIDTH} height={NEUTRAL_EMPTY_IMAGE_HEIGHT} static={true} priority={true} animation="none" objectFit="contain" style={{
            width: CPREPAGE_TRIANGLE_RENDERED_WIDTH,
            height: 'auto',
            display: 'block',
        }}/>
            </motion.div>
        </motion.div>
        ;
}
